import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { trpc } from '../lib/trpc';
import type { Wine } from '@wineryxplorer/shared';
import { VenueWineRow } from './venueWines/VenueWineRow';
import { Button } from './ui/Button';
import { FormSection } from './ui/FormSection';

/** Which status list the venue-wines manager is showing. */
export type WineTab = 'published' | 'draft' | 'archived';

const TABS: { id: WineTab; label: string }[] = [
  { id: 'published', label: 'Published' },
  { id: 'draft', label: 'Draft' },
  { id: 'archived', label: 'Archived' },
];

const EMPTY_STATE_COPY: Record<WineTab, string> = {
  published:
    'Nothing here is visible to the public yet. Create a wine, then set its status to Published on its edit page.',
  draft: 'Wines you are still working on will appear here.',
  archived: 'Wines you have retired from the list will appear here.',
};

interface VenueWinesManagementProps {
  venueId: string;
}

export const VenueWinesManagement = ({ venueId }: VenueWinesManagementProps) => {
  const navigate = useNavigate();
  const [deletingWineId, setDeletingWineId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<WineTab>('published');
  const utils = trpc.useContext();

  // Fetch wines filtered by status on the server side for better performance
  const { data: wines, isPending, error } = trpc.wine.listByVenue.useQuery({
    venueId,
    status: activeTab,
  });

  const deleteWine = trpc.wine.delete.useMutation({
    onSuccess: () => {
      // Invalidate and refetch wines for current tab
      utils.wine.listByVenue.invalidate({ venueId, status: activeTab });
      setDeletingWineId(null);
    },
    onError: (error) => {
      alert(`Error deleting wine: ${error.message}`);
      setDeletingWineId(null);
    },
  });

  const handleCreateWine = () => {
    navigate(`/wines/new?venueId=${venueId}`);
  };

  const handleEditWine = (wineId: string) => {
    // `from=manage` tells the edit page this flow started at the hub, so it
    // returns here on save or cancel instead of the wine's own page.
    navigate(`/wines/${wineId}/edit?from=manage`);
  };

  const handleDeleteWine = (wineId: string) => {
    if (confirm('Are you sure you want to delete this wine? This action cannot be undone.')) {
      setDeletingWineId(wineId);
      deleteWine.mutate({ id: wineId });
    }
  };

  if (isPending) {
    return (
      <div className="py-8">
        <p className="text-gray-600">Loading wines...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-8">
        <p className="text-error">Error loading wines: {error.message}</p>
      </div>
    );
  }

  const sortedWines = (wines?.items ?? []) as Wine[];
  const totalCount = wines?.total ?? 0;

  return (
    <div className="space-y-6">
      {/* Header with Create Button */}
      <FormSection
        title="Your Wines"
        titleAs="h3"
        // Leads its card on the venue management hub, so it draws no rule.
        divider={false}
        className="mt-0"
        description={`${totalCount} ${activeTab} ${totalCount === 1 ? 'wine' : 'wines'}`}
        actions={<Button onClick={handleCreateWine}>Create Wine</Button>}
      />

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8" aria-label="Wine status tabs">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              aria-current={activeTab === tab.id ? 'page' : undefined}
              className={`
                whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                ${
                  activeTab === tab.id
                    ? 'border-primary-700 text-primary-800'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }
              `}
            >
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Wines List */}
      {sortedWines.length > 0 ? (
        <div>
          <div className="overflow-hidden rounded-lg border border-gray-200">
            {sortedWines.map((wine) => (
              <VenueWineRow
                key={wine._id}
                wine={wine}
                isDeleting={deletingWineId === wine._id}
                onEdit={handleEditWine}
                onDelete={handleDeleteWine}
              />
            ))}
          </div>
          <p className="mt-3 text-sm text-gray-600">
            To publish, archive, or restore a wine, open it with Edit and change its status there.
          </p>
        </div>
      ) : (
        <div className="rounded-lg border border-gray-200 bg-gray-100 px-6 py-12 text-center">
          <h3 className="font-serif text-lg text-gray-900">No {activeTab} wines</h3>
          <p className="mx-auto mt-2 max-w-md text-sm text-gray-600">
            {EMPTY_STATE_COPY[activeTab]}
          </p>
          {activeTab === 'published' && (
            <div className="mt-6">
              <Button onClick={handleCreateWine}>Create Your First Wine</Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
